import { Routes, Route, BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { HelmetProvider } from 'react-helmet-async';
import React, { useEffect, Suspense, lazy } from 'react';
import { Provider } from 'react-redux';
import { CircularProgress, Box, ThemeProvider, CssBaseline } from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import theme from './theme';

import { InteractionRequiredAuthError, EventType, InteractionType } from '@azure/msal-browser';
import { AuthenticatedTemplate, useMsal, useMsalAuthentication } from '@azure/msal-react';
import { ProtectedRoute } from './app/components/shared/ProtectedRoute';
import { b2cPolicies, msalConfig } from './configs/msalAuthConfigs';
import { protectedResources } from 'configs/msalAuthConfigs';
import { persistor, store } from './store';
import { request } from 'utils/request';
import URL from './data/url';

// Lazy load components for code splitting
const SupplierClosedInvoiceDetails = lazy(() => import('app/pages/closedPurchaseOrder/SupplierClosedInvoiceDetails'));
const SupplierInvoiceDetails = lazy(() => import('app/pages/outstandingPurchaseOrder/SupplierInvoiceDetails'));
const SearchClosedPurchase = lazy(() => import('./app/pages/closedPurchaseOrder/SearchClosedPurchase'));
const EditSupplierDetails = lazy(() => import('app/pages/adminstration/suppliers/EditSupplierDetails'));
const AddSupplierDetails = lazy(() => import('app/pages/adminstration/suppliers/AddSupplierDetails'));
const TermsAndConditions = lazy(() => import('app/pages/complianceStatements/TermsAndConditions'));
const OrdersTabOfUsers = lazy(() => import('app/components/modules/myJobs/OrderUsersTab').then(m => ({ default: m.OrdersTabOfUsers })));
const CategoryManagement = lazy(() => import('app/pages/adminstration/categoryManagement'));
const ProductManagement = lazy(() => import('app/pages/adminstration/productManagement'));
const ProductCatalogue = lazy(() => import('app/pages/adminstration/productCatalogue'));
const AddProduct = lazy(() => import('app/pages/adminstration/productCatalogue/AddProduct'));
const EditProduct = lazy(() => import('app/pages/adminstration/productCatalogue/EditProduct'));
const CookiePolicy = lazy(() => import('app/pages/complianceStatements/CookiePolicy'));
const PrivacyNotice = lazy(() => import('app/pages/complianceStatements/PrivacyNotice'));
const AccountTabs = lazy(() => import('app/pages/adminstrationAccount/AccountTabs').then(m => ({ default: m.AccountTabs })));
const HomePage = lazy(() => import('./app/pages/home'));
const AddAccounts = lazy(() => import('app/pages/adminstrationAccount/AddAccount').then(m => ({ default: m.AddAccounts })));
const UnallocatedRFQ = lazy(() => import('./app/components/modules/unallocatedrfq'));
const SupplierUsers = lazy(() => import('./app/pages/adminstration/supplierUsers'));
const Search = lazy(() => import('./app/pages/outstandingPurchaseOrder/Search'));
const ClientUsers = lazy(() => import('./app/pages/adminstration/clientUsers'));
const StaffUsers = lazy(() => import('./app/pages/adminstration/staffUsers'));
const MyJobs = lazy(() => import('./app/components/modules/myJobs/myJobsList'));
const RFQSupplierView = lazy(() => import('./app/pages/RFQSupplierView/index'));
const AdminSuppliers = lazy(() => import('app/pages/adminstration/suppliers'));
const RequestForQuote = lazy(() => import('./app/components/modules/myJobs'));
const Account = lazy(() => import('./app/pages/adminstrationAccount/index'));
const NotFoundPage = lazy(() => import('./app/components/notFoundPage').then(m => ({ default: m.NotFoundPage })));
const RFQPage = lazy(() => import('./app/pages/RFQSupplierView/RFQPage'));
const SideBar = lazy(() => import('./app/components/modules/sideBar'));
const RFQSearch = lazy(() => import('./app/pages/rfqSearch'));
const PurchaseOrdersSearch = lazy(() => import('./app/pages/purchaseOrdersSearch'));
const Profile = lazy(() => import('./app/pages/profile').then(m => ({ default: m.Profile })));
const Invoice = lazy(() => import('./app/pages/Invoice'));
const CreditNote = lazy(() => import('./app/pages/CreditNote'));
const Header = lazy(() => import('./app/pages/header'));

// Loading component
const LoadingSpinner = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
    <CircularProgress />
  </Box>
);

const App = () => {
  //  MSAL
  const { login, error } = useMsalAuthentication(InteractionType.Redirect);
  const { instance } = useMsal();
  const accounts = instance.getAllAccounts();

  if (accounts.length > 0) {
    //  Set Active Account in msal for token
    instance.setActiveAccount(accounts[0]);
  }
  useEffect(() => {
    if (error instanceof InteractionRequiredAuthError) {
      login(InteractionType.Popup);
    }
  }, [error, login]);

  useEffect(() => {
    const callbackId = instance.addEventCallback(event => {
      if (event.eventType === EventType.LOGIN_FAILURE) {
        if (event.error && event.error.errorMessage.indexOf('AADB2C90118') > -1) {
          if (event.interactionType === InteractionType.Redirect) {
            instance.loginRedirect({ scopes: [process.env.REACT_APP_SCOPES || ''], authority: msalConfig.auth.forgotPassword });
          } else if (event.interactionType === InteractionType.Popup) {
            instance.loginPopup({ scopes: [process.env.REACT_APP_SCOPES || ''], authority: msalConfig.auth.forgotPassword }).catch(e => {
              return;
            });
          }
        } else if (event.error && event.error.errorMessage.indexOf('AADB2C90091') > -1) {
          // user cancelled email verification (redirect to login page)
          instance.loginRedirect();
        }
      }
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        try {
          request(`${protectedResources.api.apiEndpoint}/User/UpdateUserDetails`, {
            method: 'POST',
          });
        } catch (responseError) {
          console.error('Error while logging in' + responseError);
        }
        if (event?.payload) {
          /**
           * We need to reject id tokens that were not issued with the default sign-in policy.
           * "acr" claim in the token tells us what policy is used (NOTE: for new policies (v2.0), use "tfp" instead of "acr").
           * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
           */
          if (event.payload.idTokenClaims['acr'] === b2cPolicies.names.forgotPassword) {
            window.alert('Password has been reset successfully. \nPlease sign-in with your new password');
            return instance.logout();
          }
        }
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  return (
    <AuthenticatedTemplate>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <BrowserRouter>
                <HelmetProvider>
                  <div className="page-row">
                    <div className="menu-side-bar">
                      <Suspense fallback={<LoadingSpinner />}>
                        <SideBar />
                      </Suspense>
                    </div>
                    <div className="right-container">
                      <Suspense fallback={<LoadingSpinner />}>
                        <Header />
                      </Suspense>
                      <Suspense fallback={<LoadingSpinner />}>
                        <Routes>
                          {/* Protected routes - routes that require elevated roles */}
                          <Route element={<ProtectedRoute />}>
                            <Route path="/" element={<HomePage />} />
                            <Route path={URL.REQUEST_FOR_QUOTE} element={<RequestForQuote create={true} />} />
                            <Route path="/requestedquotes" element={<RFQSupplierView />} />
                            <Route path="/orders" element={<OrdersTabOfUsers />} />
                            <Route path="/myjobs" element={<MyJobs />} />
                            <Route path="/administration/clientuser" element={<ClientUsers />} />
                            <Route path="/administration/staffusers" element={<StaffUsers />} />
                            <Route path="/administration/supplieruser" element={<SupplierUsers />} />
                            <Route path="/administration/categorymanagement/:tab" element={<CategoryManagement />} />
                            <Route path="/administration/productmanagement" element={<ProductManagement />} />
                            <Route path="/administration/productcatalogue" element={<ProductCatalogue />} />
                            <Route path="/administration/productcatalogue/addproduct" element={<AddProduct />} />
                            <Route path="/administration/productcatalogue/:productId" element={<EditProduct />} />
                            <Route path="/administration/account" element={<Account />} />
                            <Route path="/administration/account/addaccount" element={<AddAccounts />} />
                            <Route path="/administration/account/:accountid" element={<AccountTabs />} />
                            <Route path="/administration/account/:accountid/:tab" element={<AccountTabs />} />
                            <Route path="/administration/suppliers" element={<AdminSuppliers />} />
                            <Route path="/administration/suppliers/addsupplier" element={<AddSupplierDetails />} />
                            <Route path="/administration/suppliers/:supplierid" element={<EditSupplierDetails />} />
                            <Route path="/rfqsearch" element={<RFQSearch />} />
                            <Route path="/purchaseordersearch" element={<PurchaseOrdersSearch />} />
                            <Route path="/profile" element={<Profile />} />
                            <Route path="/unallocatedrfq" element={<UnallocatedRFQ />} />
                            <Route path="/readytoinvoice" element={<Invoice />} />
                            <Route path={URL.RAISE_CREDIT_NOTE} element={<CreditNote />} />
                            <Route path={`${URL.RAISE_CREDIT_NOTE}/:jobId`} element={<CreditNote />} />
                            <Route path="/myjobs/:jobId" element={<RequestForQuote create={false} />} />
                            <Route path="/myjobs/:jobId/:tab" element={<RequestForQuote create={false} />} />
                            <Route path="/outstandingpos" element={<Search />} />
                            <Route path="/outstandingpos/SupplierInvoiceDetails/:jobOrAccountId" element={<SupplierInvoiceDetails />} />
                            <Route path="/completedpos" element={<SearchClosedPurchase />} />
                            <Route path="/completedpos/SupplierInvoiceDetails/:jobId" element={<SupplierClosedInvoiceDetails />} />
                            <Route path="/requestedquotes/:productTemplateId" element={<RFQPage />} />
                            <Route path="/orders/:productTemplateId" element={<RFQPage />} />
                          </Route>
                          <Route path="/*" element={<NotFoundPage />} />
                          <Route path="/termsandconditions" element={<TermsAndConditions />} />
                          <Route path="/cookiepolicy" element={<CookiePolicy />} />
                          <Route path="/privacynotice" element={<PrivacyNotice />} />
                        </Routes>
                      </Suspense>
                    </div>
                  </div>
                </HelmetProvider>
              </BrowserRouter>
            </ThemeProvider>
          </StyledEngineProvider>
        </PersistGate>
      </Provider>
    </AuthenticatedTemplate>
  );
};

export default App;
